import { hugeOne, type HugeNumber } from "@/shared/numbers";
import { cloneExplorationState } from "./explorationState";
import {
  EXPLORATION_BIOME_IDS,
  type ExplorationAreaState,
  type ExplorationBiomeId,
  type ExplorationDefinition,
  type ExplorationProgressionEffects,
  type ExplorationState,
} from "./explorationTypes";

const PROGRESS_EPSILON_MS = 1e-6;

export interface AdvanceExplorationInput {
  readonly state: ExplorationState;
  readonly definition: ExplorationDefinition;
  readonly participation: Readonly<Record<ExplorationBiomeId, number>>;
  readonly gameElapsedMs: number;
  readonly speedMultipliers?: Partial<
    Readonly<Record<ExplorationBiomeId, number>>
  >;
}

export interface AdvanceExplorationResult {
  readonly state: ExplorationState;
  readonly effects: ExplorationProgressionEffects;
}

export type ExplorationCompletionBoundaryInput = Pick<
  AdvanceExplorationInput,
  "state" | "definition" | "participation" | "speedMultipliers"
>;

interface MutableProgressionState {
  biomeLevel: number;
  purchasedMpSlotCount: number;
  cumulativeTimAddedMs: number;
  timAccelerationEnabled: boolean;
  areas: Record<ExplorationBiomeId, ExplorationAreaState>;
}

interface MutableProgressionEffects {
  completionCounts: Record<ExplorationBiomeId, number>;
  ohovPowerAwarded: number;
  timAddedMs: number;
  hgMultiplier: HugeNumber;
  biomeLevelsGained: number;
}

interface CompletionBatchEffects {
  readonly ohovPowerAwarded: number;
  readonly timAddedMs: number;
  readonly hgMultiplier: HugeNumber;
  readonly biomeLevelsGained: number;
}

export function advanceExploration(
  input: AdvanceExplorationInput,
): AdvanceExplorationResult {
  assertFiniteNonNegative(input.gameElapsedMs, "gameElapsedMs");
  const state = cloneExplorationState(input.state) as MutableProgressionState;
  const effects = createEmptyProgressionEffects();
  const speedMultipliers = resolveSpeedMultipliers(input.speedMultipliers);
  let remainingGameElapsedMs = input.gameElapsedMs;

  settleReadyPrimaryCompletions(input, state, effects);

  while (remainingGameElapsedMs > PROGRESS_EPSILON_MS) {
    const boundary = resolveNextPrimaryBoundaryMs({
      state,
      definition: input.definition,
      participation: input.participation,
      speedMultipliers,
    });
    if (boundary === null || boundary > remainingGameElapsedMs) {
      addOrdinaryProgress({
        state,
        participation: input.participation,
        speedMultipliers,
        elapsedMs: remainingGameElapsedMs,
      });
      remainingGameElapsedMs = 0;
      settleReadyPrimaryCompletions(input, state, effects);
      break;
    }

    const elapsedToBoundary = Math.min(
      remainingGameElapsedMs,
      Math.max(0, boundary),
    );
    addOrdinaryProgress({
      state,
      participation: input.participation,
      speedMultipliers,
      elapsedMs: elapsedToBoundary,
    });
    remainingGameElapsedMs = normalizeRemainder(
      remainingGameElapsedMs - elapsedToBoundary,
    );
    settleReadyPrimaryCompletions(input, state, effects);
  }
  if (remainingGameElapsedMs > 0) {
    addOrdinaryProgress({
      state,
      participation: input.participation,
      speedMultipliers,
      elapsedMs: remainingGameElapsedMs,
    });
    settleReadyPrimaryCompletions(input, state, effects);
  }

  return { state, effects };
}

export function resolveNextExplorationCompletionElapsedMs(
  input: ExplorationCompletionBoundaryInput,
): number | null {
  return resolveNextPrimaryBoundaryMs({
    state: input.state,
    definition: input.definition,
    participation: input.participation,
    speedMultipliers: resolveSpeedMultipliers(input.speedMultipliers),
  });
}

function resolveNextPrimaryBoundaryMs(input: {
  readonly state: ExplorationState;
  readonly definition: ExplorationDefinition;
  readonly participation: Readonly<Record<ExplorationBiomeId, number>>;
  readonly speedMultipliers: Readonly<Record<ExplorationBiomeId, number>>;
}): number | null {
  let nextBoundaryMs = Number.POSITIVE_INFINITY;
  for (const biomeId of EXPLORATION_BIOME_IDS) {
    const speed =
      input.participation[biomeId] * input.speedMultipliers[biomeId];
    if (speed <= 0) continue;
    const periodMs = input.definition.areas[biomeId].periodMs;
    const progressMs = input.state.areas[biomeId].progressMs;
    nextBoundaryMs = Math.min(
      nextBoundaryMs,
      Math.max(0, (periodMs - progressMs) / speed),
    );
  }
  return Number.isFinite(nextBoundaryMs) ? nextBoundaryMs : null;
}

function addOrdinaryProgress(input: {
  readonly state: MutableProgressionState;
  readonly participation: Readonly<Record<ExplorationBiomeId, number>>;
  readonly speedMultipliers: Readonly<Record<ExplorationBiomeId, number>>;
  readonly elapsedMs: number;
}): void {
  if (input.elapsedMs <= 0) return;
  for (const biomeId of EXPLORATION_BIOME_IDS) {
    const addedMs =
      input.elapsedMs *
      input.participation[biomeId] *
      input.speedMultipliers[biomeId];
    if (addedMs <= 0) continue;
    input.state.areas[biomeId] = {
      ...input.state.areas[biomeId],
      progressMs: input.state.areas[biomeId].progressMs + addedMs,
    };
  }
}

function settleReadyPrimaryCompletions(
  input: AdvanceExplorationInput,
  state: MutableProgressionState,
  effects: MutableProgressionEffects,
): void {
  const primaryCounts = createEmptyCompletionCounts();
  for (const biomeId of EXPLORATION_BIOME_IDS) {
    if (input.participation[biomeId] <= 0) continue;
    const periodMs = input.definition.areas[biomeId].periodMs;
    const progressMs = state.areas[biomeId].progressMs;
    const count = resolveCompletedCircuitCount(progressMs, periodMs);
    if (count === 0) continue;
    primaryCounts[biomeId] = count;
    state.areas[biomeId] = {
      ...state.areas[biomeId],
      progressMs: normalizeProgress(progressMs - count * periodMs, periodMs),
    };
  }
  if (!hasCompletions(primaryCounts)) return;

  const primaryLevel = state.biomeLevel;
  const primaryEffects = resolveCompletionBatchEffects(
    input.definition,
    primaryCounts,
    primaryLevel,
  );
  applyBatchEffects(effects, primaryCounts, primaryEffects);
  state.biomeLevel += primaryEffects.biomeLevelsGained;

  if (primaryEffects.timAddedMs <= 0) return;
  state.cumulativeTimAddedMs += primaryEffects.timAddedMs;
  const bonusCounts = addTimProgressAndResolveBonusCounts({
    state,
    definition: input.definition,
    timAddedMs: primaryEffects.timAddedMs,
  });
  if (!hasCompletions(bonusCounts)) return;

  const bonusEffects = resolveCompletionBatchEffects(
    input.definition,
    bonusCounts,
    state.biomeLevel,
  );
  applyBatchEffects(effects, bonusCounts, bonusEffects);
  state.biomeLevel += bonusEffects.biomeLevelsGained;
}

function addTimProgressAndResolveBonusCounts(input: {
  readonly state: MutableProgressionState;
  readonly definition: ExplorationDefinition;
  readonly timAddedMs: number;
}): Record<ExplorationBiomeId, number> {
  const counts = createEmptyCompletionCounts();
  for (const biomeId of EXPLORATION_BIOME_IDS) {
    const periodMs = input.definition.areas[biomeId].periodMs;
    const progressMs = input.state.areas[biomeId].progressMs + input.timAddedMs;
    const count = resolveCompletedCircuitCount(progressMs, periodMs);
    // Tim receives the direct progress, but completions caused by that same
    // progress wave do not emit another Tim effect and recursively self-chain.
    if (biomeId !== "tim") counts[biomeId] = count;
    input.state.areas[biomeId] = {
      ...input.state.areas[biomeId],
      progressMs: normalizeProgress(progressMs - count * periodMs, periodMs),
    };
  }
  return counts;
}

function resolveCompletionBatchEffects(
  definition: ExplorationDefinition,
  counts: Readonly<Record<ExplorationBiomeId, number>>,
  biomeLevel: number,
): CompletionBatchEffects {
  let ohovPowerAwarded = 0;
  let timAddedMs = 0;
  let hgMultiplier = hugeOne();
  let biomeLevelsGained = 0;

  for (const biomeId of EXPLORATION_BIOME_IDS) {
    const count = counts[biomeId];
    if (count === 0) continue;
    const effect = definition.areas[biomeId].resolveEffect(biomeLevel);
    switch (effect.type) {
      case "award-ohov-power":
        ohovPowerAwarded += effect.amount * count;
        break;
      case "add-exploration-time":
        timAddedMs += effect.milliseconds * count;
        break;
      case "multiply-hg":
        hgMultiplier = hgMultiplier.mul(effect.multiplier.pow(count));
        break;
      case "raise-biome-level":
        biomeLevelsGained += effect.amount * count;
        break;
    }
  }
  return { ohovPowerAwarded, timAddedMs, hgMultiplier, biomeLevelsGained };
}

function applyBatchEffects(
  effects: MutableProgressionEffects,
  counts: Readonly<Record<ExplorationBiomeId, number>>,
  batch: CompletionBatchEffects,
): void {
  for (const biomeId of EXPLORATION_BIOME_IDS) {
    effects.completionCounts[biomeId] += counts[biomeId];
  }
  effects.ohovPowerAwarded += batch.ohovPowerAwarded;
  effects.timAddedMs += batch.timAddedMs;
  effects.hgMultiplier = effects.hgMultiplier.mul(batch.hgMultiplier);
  effects.biomeLevelsGained += batch.biomeLevelsGained;
}

function createEmptyProgressionEffects(): MutableProgressionEffects {
  return {
    completionCounts: createEmptyCompletionCounts(),
    ohovPowerAwarded: 0,
    timAddedMs: 0,
    hgMultiplier: hugeOne(),
    biomeLevelsGained: 0,
  };
}

function createEmptyCompletionCounts(): Record<ExplorationBiomeId, number> {
  return { hov: 0, tim: 0, mat: 0, lev: 0 };
}

function hasCompletions(
  counts: Readonly<Record<ExplorationBiomeId, number>>,
): boolean {
  return EXPLORATION_BIOME_IDS.some((biomeId) => counts[biomeId] > 0);
}

function resolveCompletedCircuitCount(
  progressMs: number,
  periodMs: number,
): number {
  return Math.max(0, Math.floor((progressMs + PROGRESS_EPSILON_MS) / periodMs));
}

function normalizeProgress(progressMs: number, periodMs: number): number {
  if (Math.abs(progressMs) <= PROGRESS_EPSILON_MS) return 0;
  if (progressMs < 0 && progressMs >= -PROGRESS_EPSILON_MS) return 0;
  if (periodMs - progressMs <= PROGRESS_EPSILON_MS) return 0;
  return progressMs;
}

function normalizeRemainder(value: number): number {
  return Math.abs(value) <= PROGRESS_EPSILON_MS ? 0 : value;
}

function resolveSpeedMultipliers(
  input: Partial<Readonly<Record<ExplorationBiomeId, number>>> | undefined,
): Readonly<Record<ExplorationBiomeId, number>> {
  return Object.fromEntries(
    EXPLORATION_BIOME_IDS.map((biomeId) => {
      const value = input?.[biomeId] ?? 1;
      assertFiniteNonNegative(value, `speedMultipliers.${biomeId}`);
      return [biomeId, value];
    }),
  ) as Record<ExplorationBiomeId, number>;
}

function assertFiniteNonNegative(value: number, name: string): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be finite and non-negative.`);
  }
}
